import dayjs from 'dayjs'

import type { Trend } from 'entities/trend'
import { type TranslateFunction } from 'lib/tolgee'

import { trendTerritoryTitleTranslations } from './translations'

export const getTrendsDescriptionsMap = (t: TranslateFunction, trends: Trend[]): Map<string, string> => {
  const trendsDescriptions = new Map<string, string>()
  const trendNewList = trends.filter(trend => trend.status === 'new')
  const trendTrendingList = trends.filter(trend => trend.status === 'trending')
  const mostTotalPosts = Math.max(...trends.slice(1).map(t => t.totalPosts))
  const fewestTotalPosts = Math.min(...trends.slice(1).map(t => t.totalPosts))

  const today = dayjs()

  const usedRanksList = new Set()
  let isNewFourAlreadyUsed = false
  let isNewFiveAlreadyUsed = false
  let isTrendingFourAlreadyUsed = false
  let isTrendingFiveAlreadyUsed = false

  const descriptions = trends.map((trend, index) => {
    const rank = index + 1
    const hasMostVideos = trend.totalPosts >= mostTotalPosts
    const hasFewestVideos = trend.totalPosts <= fewestTotalPosts

    const createdAt = dayjs(trend.createdAt)
    const differenceDays = Math.max(today.diff(createdAt, 'days'), 1)

    const formattedCreatedAt = createdAt.format('DD/MM/YYYY')

    if (trend.status === 'new') {
      const isFirstOfNew = trendNewList[0].id === trend.id

      if (isFirstOfNew) {
        return t(trendTerritoryTitleTranslations.new1)
      }

      if (hasMostVideos) return t(trendTerritoryTitleTranslations.new2)

      if (hasFewestVideos) return t(trendTerritoryTitleTranslations.new3)

      if (!usedRanksList.has(rank) && !hasMostVideos && !hasFewestVideos && !isNewFourAlreadyUsed) {
        isNewFourAlreadyUsed = true
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.new4({ rank }))
      }

      if (
        !usedRanksList.has(rank) &&
        !hasMostVideos &&
        !hasFewestVideos &&
        isNewFourAlreadyUsed &&
        !isNewFiveAlreadyUsed
      ) {
        isNewFiveAlreadyUsed = true
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.new5)
      }

      if (!usedRanksList.has(rank) && !hasMostVideos && !hasFewestVideos) {
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.new6({ createdAt: formattedCreatedAt }))
      }
    }

    if (trend.status === 'trending') {
      const isFirstOfTrending = trendTrendingList[0].id === trend.id
      if (isFirstOfTrending) {
        return t(trendTerritoryTitleTranslations.trending1)
      }

      if (hasMostVideos) return t(trendTerritoryTitleTranslations.trending2)

      if (hasFewestVideos) return t(trendTerritoryTitleTranslations.trending3({ postCount: trend.totalPosts }))

      if (!usedRanksList.has(rank) && !hasMostVideos && !hasFewestVideos && !isTrendingFourAlreadyUsed) {
        isTrendingFourAlreadyUsed = true
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.trending4({ activeDays: differenceDays }))
      }

      if (
        !usedRanksList.has(rank) &&
        !hasMostVideos &&
        !hasFewestVideos &&
        isTrendingFourAlreadyUsed &&
        !isTrendingFiveAlreadyUsed
      ) {
        isTrendingFiveAlreadyUsed = true
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.trending5({ activeDays: differenceDays }))
      }

      if (!usedRanksList.has(rank) && !hasMostVideos && !hasFewestVideos) {
        usedRanksList.add(rank)
        return t(trendTerritoryTitleTranslations.trending6({ rank }))
      }
    }

    if (hasMostVideos) return t(trendTerritoryTitleTranslations.empty1)

    return t(trendTerritoryTitleTranslations.empty3)
  })

  trends.forEach((trend, index) => {
    trendsDescriptions.set(trend.id, descriptions[index])
  })

  return trendsDescriptions
}
